import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Menu, Icon, Tooltip } from 'antd'
import styled from 'styled-components'
import { IconFolders, IconMyDistrict } from '@edulastic/icons'
import { SMART_FILTERS } from '@edulastic/constants/const/filters'
import { TRACK_IDS } from '@edulastic/constants/educationJoyrides'
import {
  white,
  mainTextColor,
  smallDesktopWidth,
  themeColor,
  themeColorBlue,
} from '@edulastic/colors'
import { connect } from 'react-redux'
import _ from 'lodash'
import { onEnterKeyPressHandler } from '../../../../../common/utils/helpers'
import EducationJoyride from '../../EducationJoyride'
import { StyledInfoIcon } from '../../../../ItemList/components/ItemFilter/styled'
import {
  getOrgItemBanksSelector,
  getUserDistrict,
  getUserFeatures,
} from '../../../selectors/user'
import { isNonPaidPremiumId } from '../../../../utils/nonPaidPremiumCollections'

const DEFAULT_VISIBLE_FILTERS_COUNT = 4

const hasCertifiedItemBanks = (itemBanks = []) =>
  itemBanks.some(
    ({ type, _id }) =>
      type === 'custom' || (type === 'premium' && !isNonPaidPremiumId(_id))
  )

const isDistrictLibrary = (value, checkType = 'item') => {
  if (checkType === 'filter') {
    return (
      value === SMART_FILTERS.CERTIFIED_CONTENT ||
      value === SMART_FILTERS.DISTRICT_CERTIFIED_LIBRARY
    )
  }
  return value?.icon === 'myDistrictsLibrary'
}

const getCertifiedContentPath = (districtItem) =>
  districtItem?.children?.find(
    (child) => child.filter === SMART_FILTERS.CERTIFIED_CONTENT
  )?.path

const TestFiltersNav = ({
  items,
  onSelect,
  search = {},
  features,
  itemBanks,
  district,
  libraryFolderExpanded,
}) => {
  const hasCertifiedContent = useMemo(() => hasCertifiedItemBanks(itemBanks), [
    itemBanks,
  ])

  const showDistrictLibraryFilter = useMemo(() => {
    const { enableDistrictContentFilter } = features
    const { districtId } = district
    return Boolean(enableDistrictContentFilter && districtId)
  }, [features, district])

  const [districtLibraryExpanded, setDistrictLibraryExpanded] = useState(() =>
    isDistrictLibrary(search.filter, 'filter')
  )

  useEffect(() => {
    setDistrictLibraryExpanded(isDistrictLibrary(search.filter, 'filter'))
  }, [search.filter])

  const updatedItems = useMemo(() => {
    let updatedItemsReturn = _.cloneDeep(items)

    if (!showDistrictLibraryFilter) {
      updatedItemsReturn = updatedItemsReturn.filter(
        (item) => !isDistrictLibrary(item)
      )
    } else {
      updatedItemsReturn = updatedItemsReturn.map((item) => {
        if (!isDistrictLibrary(item) || hasCertifiedContent) {
          return item
        }
        const { children, ...flatDistrictLibrary } = item
        return {
          ...flatDistrictLibrary,
          filter: SMART_FILTERS.DISTRICT_CERTIFIED_LIBRARY,
        }
      })
    }

    return libraryFolderExpanded
      ? updatedItemsReturn
      : updatedItemsReturn.slice(0, DEFAULT_VISIBLE_FILTERS_COUNT)
  }, [
    items,
    showDistrictLibraryFilter,
    hasCertifiedContent,
    libraryFolderExpanded,
  ])

  const selected = useMemo(() => {
    let selectedPath = updatedItems[0]?.path
    if (search.filter) {
      const childFilterMenuItems = _.flatten(
        updatedItems.map((v) => v.children || [])
      )
      const allFilterMenuItems = [...childFilterMenuItems, ...updatedItems]
      const matches = allFilterMenuItems.filter(
        (item) => item.filter === search.filter
      )
      const childMatch = matches.find((item) =>
        childFilterMenuItems.some((child) => child.path === item.path)
      )
      const getCurrent = childMatch || matches[0] || {}
      selectedPath = getCurrent?.path || selectedPath
    }
    return selectedPath
  }, [updatedItems, search.filter])

  const handleNavSelect = useCallback(
    (selectInfo) => {
      const { key } = selectInfo

      if (hasCertifiedContent) {
        const districtItem = updatedItems.find((item) =>
          isDistrictLibrary(item)
        )
        const certifiedPath = getCertifiedContentPath(districtItem)

        if (districtItem && key === districtItem.path && certifiedPath) {
          onSelect({ ...selectInfo, key: certifiedPath })
          return
        }
      }

      onSelect(selectInfo)
    },
    [hasCertifiedContent, onSelect, updatedItems]
  )

  const iconNameToComponent = {
    folders: <IconFolders className="anticon" aria-hidden="true" />,
    myDistrictsLibrary: (
      <IconMyDistrict
        style={{
          marginRight: '1rem',
        }}
        aria-hidden="true"
      />
    ),
  }

  const handleDistrictLibraryExpand = useCallback(
    (e) => {
      e.stopPropagation()
      e.preventDefault()
      const districtItem = updatedItems.find((item) => isDistrictLibrary(item))
      const certifiedPath = getCertifiedContentPath(districtItem)
      if (districtItem && certifiedPath) {
        onSelect({ key: certifiedPath })
      }
    },
    [onSelect, updatedItems]
  )

  const renderItemIcon = (icon) => {
    if (!icon) {
      return null
    }
    if (icon in iconNameToComponent) {
      return iconNameToComponent[icon]
    }
    return <Icon type={icon} aria-hidden="true" />
  }

  return (
    <>
      <Container onSelect={handleNavSelect} selectedKeys={[selected]}>
        {updatedItems.map((item) => {
          const isDistrictLibraryMenuItem = isDistrictLibrary(item)
          const isDistrictLibraryExpandable =
            isDistrictLibraryMenuItem && hasCertifiedContent
          const districtSubItems =
            isDistrictLibraryExpandable && districtLibraryExpanded
              ? item.children
              : null

          return (
            <Item
              data-cy={item.text}
              key={item.path}
              data-joyride-id={
                isDistrictLibraryMenuItem ? 'districtLibraryFilter' : undefined
              }
              marginBottom={
                districtSubItems?.length
                  ? `${districtSubItems.length * 3}rem`
                  : 'auto'
              }
              wrap="wrap"
              tabIndex={0}
              onKeyDown={(e) =>
                onEnterKeyPressHandler(e, () =>
                  handleNavSelect({ key: item.path })
                )
              }
              aria-current={item.path === selected ? 'page' : undefined}
              aria-expanded={
                isDistrictLibraryExpandable
                  ? districtLibraryExpanded
                  : undefined
              }
            >
              {renderItemIcon(item.icon)}
              <span
                tabIndex={0}
                role="link"
                className={item.text === 'FOLDERS' ? 'folders' : ''}
                onKeyDown={(e) =>
                  onEnterKeyPressHandler(e, () =>
                    handleNavSelect({ key: item.path })
                  )
                }
                aria-current={item.path === selected}
              >
                {item.text}
              </span>
              {isDistrictLibraryExpandable && !districtLibraryExpanded ? (
                <ExpandIcon
                  type="down"
                  role="button"
                  tabIndex={0}
                  aria-label="Expand District Library"
                  onClick={handleDistrictLibraryExpand}
                  onKeyDown={(e) =>
                    onEnterKeyPressHandler(e, handleDistrictLibraryExpand)
                  }
                />
              ) : null}
              {districtSubItems?.length ? (
                <Container
                  selectedKeys={[selected]}
                  subMenu
                  onSelect={handleNavSelect}
                  onClick={({ domEvent }) => {
                    domEvent.stopPropagation()
                    domEvent.preventDefault()
                  }}
                >
                  {districtSubItems.map((subItem) => (
                    <Item isSubItem data-cy={subItem.text} key={subItem.path}>
                      <SubItemLabel>
                        <span
                          style={{
                            fontSize: '10.5px',
                          }}
                        >
                          {subItem.text}
                        </span>
                        {subItem.tooltip ? (
                          <Tooltip title={subItem.tooltip} placement="right">
                            <StyledInfoIcon
                              tabIndex={0}
                              aria-label={`${subItem.text} info`}
                            />
                          </Tooltip>
                        ) : null}
                      </SubItemLabel>
                    </Item>
                  ))}
                </Container>
              ) : null}
            </Item>
          )
        })}
      </Container>
      {showDistrictLibraryFilter ? (
        <EducationJoyride
          trackId={TRACK_IDS.SEARCH_DISTRICT_LIBRARY}
          featureGate={showDistrictLibraryFilter}
        />
      ) : null}
    </>
  )
}

TestFiltersNav.propTypes = {
  items: PropTypes.array.isRequired,
  onSelect: PropTypes.func,
}

TestFiltersNav.defaultProps = {
  onSelect: () => {},
}

const enhance = connect((state) => ({
  features: getUserFeatures(state),
  itemBanks: getOrgItemBanksSelector(state),
  district: getUserDistrict(state),
}))

export default enhance(TestFiltersNav)

const Container = styled(Menu)`
  border-right: none;
  background: transparent;
  margin-bottom: 5px;
  ${({ subMenu }) =>
    subMenu
      ? `
  margin-top: 4px !important;
  margin-left: 0 !important;
  padding-left: 0;
  width: 100%;
  flex-basis: 100%;
  `
      : ''}
`

const ExpandIcon = styled(Icon)`
  &&& {
    font-size: 12px;
    margin-left: 8px;
    margin-right: 0;

    svg {
      width: 12px;
      height: 12px;
    }
  }
`

const SubItemLabel = styled.span`
  display: inline-flex;
  align-items: center;
  text-transform: uppercase;
  white-space: nowrap;
  margin-left: 10px;
`

const Item = styled(Menu.Item)`
  color: ${mainTextColor};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
  margin-bottom: ${({ marginBottom }) => marginBottom || 'auto'}!important;
  margin-left: ${({ isSubItem }) => (isSubItem ? '2.5rem' : 'auto')}!important;
  padding-left: ${({ isSubItem }) => (isSubItem ? '0' : 'inherit')}!important;
  padding-right: ${({ isSubItem }) =>
    isSubItem ? '10px' : 'inherit'}!important;
  width: ${({ isSubItem }) => (isSubItem ? 'fit-content' : 'auto')}!important;
  max-width: ${({ isSubItem }) => (isSubItem ? '100%' : 'none')};
  align-self: ${({ isSubItem }) => (isSubItem ? 'flex-start' : 'auto')};
  box-sizing: ${({ isSubItem }) => (isSubItem ? 'border-box' : 'content-box')};
  flex-wrap: ${({ wrap }) => wrap || 'nowrap'};
  overflow: visible !important;

  .anticon {
    font-size: 18px;
    margin-right: 22px;
  }

  :hover {
    color: ${themeColor};

    svg {
      fill: ${themeColor};
    }
  }

  &.ant-menu-item {
    font-size: 11px;
    display: flex;
    align-items: center;
    text-transform: uppercase;
    ${({ isSubItem }) =>
      isSubItem
        ? `
      &&& {
        padding-left: 0 !important;
        padding-right: 10px !important;
        margin-left: 2.5rem !important;
        width: fit-content !important;
        max-width: 100%;
        align-self: flex-start;
        box-sizing: border-box;
      }
    `
        : `
      padding-left: 24px !important;
    `}
    .folders:hover {
      color: ${themeColorBlue};
    }

    @media (max-width: ${smallDesktopWidth}) {
      height: 30px;
      line-height: 30px;
      svg {
        width: 17px;
        height: 17px;
      }
    }
  }

  &.ant-menu-item-selected {
    border-left: 3px solid ${themeColor} !important;
    background-color: ${white} !important;
    color: ${themeColor};
    box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.1);
    border-radius: 0px 10px 10px 0px;
    ${({ isSubItem }) =>
      isSubItem
        ? `
      &&& {
        padding-left: 0 !important;
        padding-right: 10px !important;
        margin-left: 2.5rem !important;
        width: fit-content !important;
        box-sizing: border-box;
      }
    `
        : `
      padding-left: 24px !important;
    `}
    &:hover {
      color: ${themeColorBlue};
    }
    svg {
      fill: ${themeColor};
    }
  }
`
